import React, { useState, useEffect, useRef } from "react";
import Modal from "./Modal";
import PrimaryButton from "../components/Buttons/PrimaryButton";
import LoadingIcons from "react-loading-icons";
import { searchUsers } from "../utils/Api";

interface AddMemberModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  newMemberEmail: string;
  setNewMemberEmail: (email: string) => void;
  newMemberRole: string;
  setNewMemberRole: (role: string) => void;
  addMemberError: string | null;
  addMemberLoading: boolean;
  onAddMember: () => void;
}

interface UserSuggestion {
  email: string;
  name: string;
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  isOpen,
  setIsOpen,
  newMemberEmail,
  setNewMemberEmail,
  newMemberRole,
  setNewMemberRole,
  addMemberError,
  addMemberLoading,
  onAddMember,
}) => {
  const [suggestions, setSuggestions] = useState<UserSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSuggestions]);

  useEffect(() => {
    if (!isOpen) {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [isOpen]);

  const handleEmailChange = (email: string) => {
    setNewMemberEmail(email);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (email.length >= 2) {
      setSearchLoading(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await searchUsers(email);
          setSuggestions(response.data.users || []);
          setShowSuggestions(true);
        } catch (error) {
          console.error("Failed to search users:", error);
          setSuggestions([]);
        } finally {
          setSearchLoading(false);
        }
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setSearchLoading(false);
    }
  };

  const handleSelectSuggestion = (suggestion: UserSuggestion) => {
    setNewMemberEmail(suggestion.email);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <div className="sm:flex sm:items-start">
        <div className="mt-3 sm:mt-0 text-left w-full">
          <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
            Add member
          </h3>

          {addMemberError && (
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
              role="alert"
            >
              {addMemberError}
            </div>
          )}

          <div className="mb-4 relative">
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Email address
            </label>
            <input
              ref={inputRef}
              type="email"
              id="email"
              value={newMemberEmail}
              onChange={(e) => handleEmailChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="member@example.com"
              disabled={addMemberLoading}
              autoComplete="off"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div
                ref={suggestionsRef}
                className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto"
              >
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelectSuggestion(suggestion)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium text-gray-900">
                      {suggestion.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {suggestion.email}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {searchLoading && (
              <div className="absolute right-3 top-[38px] flex items-center">
                <LoadingIcons.TailSpin
                  stroke="#9CA3AF"
                  width="16"
                  height="16"
                />
              </div>
            )}
          </div>

          <div className="mb-4">
            <label
              htmlFor="role"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Role
            </label>
            <select
              id="role"
              value={newMemberRole}
              onChange={(e) => setNewMemberRole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={addMemberLoading}
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-2">
            <PrimaryButton
              onClick={onAddMember}
              disabled={addMemberLoading}
              className="w-full sm:w-auto"
            >
              {addMemberLoading ? (
                <span className="flex items-center gap-2">
                  <LoadingIcons.TailSpin stroke="#fff" width="16" height="16" />
                  Adding...
                </span>
              ) : (
                "Add Member"
              )}
            </PrimaryButton>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              disabled={addMemberLoading}
              className="mt-3 sm:mt-0 w-full sm:w-auto rounded-md bg-white px-4 py-2 font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddMemberModal;
